import React, { useState, useEffect } from 'react';
import { toChileDate } from '../../utils/chileTime';
import { dashboardApi, enrollmentsApi, certificatesApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Chalkboard,
  Certificate,
  ClipboardText,
  Clock,
  Calendar,
  ArrowRight,
  CheckCircle
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, title, value, color = '#2BBAA4' }) => (
  <Card className="bg-white border-[#2B4B8C]/10">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[#2B4B8C]/60 uppercase tracking-wider mb-1">{title}</p>
          <p className="text-3xl font-bold text-[#2B4B8C]">{value}</p>
        </div>
        <div 
          className="w-12 h-12 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon size={24} style={{ color }} weight="duotone" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function WorkerDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await dashboardApi.worker();
      setStats(res.data);
    } catch (error) {
      toast.error('Error al cargar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Pendiente', className: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
      confirmed: { label: 'Confirmada', className: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
      attended: { label: 'Asistió', className: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
      completed: { label: 'Completada', className: 'bg-[#2BBAA4]/10 text-[#2BBAA4] border-[#2BBAA4]/20' },
    };
    const c = config[status] || config.pending;
    return <Badge className={`${c.className} border`}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="worker-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#2B4B8C]">Mi Dashboard</h1>
        <p className="text-[#2B4B8C]/60 mt-1">Resumen de tus capacitaciones y certificados</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Chalkboard}
          title="Capacitaciones"
          value={stats?.total_trainings || 0}
          color="#2B4B8C"
        />
        <StatCard
          icon={CheckCircle}
          title="Completadas"
          value={stats?.completed_trainings || 0}
          color="#2BBAA4"
        />
        <StatCard
          icon={ClipboardText}
          title="Evaluaciones Pendientes"
          value={stats?.pending_evaluations || 0}
          color="#F59E0B"
        />
        <StatCard
          icon={Certificate}
          title="Certificados"
          value={stats?.certificates_earned || 0}
          color="#8B5CF6"
        />
      </div>

      {/* Upcoming Trainings */}
      <Card className="bg-white border-[#2B4B8C]/10">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-[#2B4B8C] flex items-center gap-2">
            <Calendar size={20} className="text-[#2BBAA4]" />
            Próximas Capacitaciones
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-[#2BBAA4] hover:text-[#2BBAA4]/80"
            onClick={() => navigate('/worker/trainings')}
          >
            Ver todas <ArrowRight className="ml-1" size={16} />
          </Button>
        </CardHeader>
        <CardContent>
          {stats?.upcoming_trainings?.length > 0 ? (
            <div className="space-y-4">
              {stats.upcoming_trainings.map((training) => (
                <div 
                  key={training.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-[#f8fafc] hover:bg-[#f1f5f9] transition-colors cursor-pointer"
                  onClick={() => navigate('/worker/trainings')}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#2B4B8C]/20 flex items-center justify-center">
                      <Chalkboard size={24} className="text-[#2B4B8C]" />
                    </div>
                    <div>
                      <p className="text-[#2B4B8C] font-medium">{training.name}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-[#2B4B8C]/50 flex items-center gap-1">
                          <Calendar size={12} />
                          {format(toChileDate(training.scheduled_date), "dd MMM yyyy", { locale: es })}
                        </span>
                        <span className="text-xs text-[#2B4B8C]/50 flex items-center gap-1">
                          <Clock size={12} />
                          {format(toChileDate(training.scheduled_date), "HH:mm")}
                        </span>
                      </div>
                    </div>
                  </div>
                  {getStatusBadge(training.status)}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[#2B4B8C]/50">
              No tienes capacitaciones próximas
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Button 
          variant="outline" 
          className="h-auto py-6 flex flex-col gap-2 border-[#2B4B8C]/10 hover:border-[#2BBAA4]/50 hover:bg-[#2BBAA4]/5"
          onClick={() => navigate('/worker/trainings')}
        >
          <Chalkboard size={24} className="text-[#2BBAA4]" />
          <span>Mis Capacitaciones</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-6 flex flex-col gap-2 border-[#2B4B8C]/10 hover:border-[#F59E0B]/50 hover:bg-[#F59E0B]/5"
          onClick={() => navigate('/worker/evaluations')}
        >
          <ClipboardText size={24} className="text-[#F59E0B]" />
          <span>Mis Evaluaciones</span>
        </Button>
        <Button 
          variant="outline" 
          className="h-auto py-6 flex flex-col gap-2 border-[#2B4B8C]/10 hover:border-[#8B5CF6]/50 hover:bg-[#8B5CF6]/5"
          onClick={() => navigate('/worker/certificates')}
        >
          <Certificate size={24} className="text-[#8B5CF6]" />
          <span>Mis Certificados</span>
        </Button>
      </div>
    </div>
  );
}
